import React, { useContext } from 'react';
import { useParams } from 'react-router-dom';
import SecondaryHeader from '@app/components/header-secondary';
import { numberFormat, useLocation } from '@app/utils';
import {
  Container,
  BackgroundContainer,
  Loader,
  MunicipalityContainer,
  Heading,
  InfoCard,
  InfoItem,
  PrimaryInfoItem,
  Label,
  Value,
  IconItem,
  MuncipalityIcon,
  PrimaryInfoCard,
  EmailInfoItem,
  InfoBox,
  PrimaryHeading,
  DetailsBox,
  InstCard,
  PrimaryInfoBox,
  TaxRate,
  PrimaryDetailsBox,
  NumText,
  MultiInfoCard,
  DetailsCard,
  MunicipalityLogo,
  LogoInfoCard,
  LogoIcon,
  Counties,
} from './municipalities-details-components';
import { AuthContext } from '@app/providers';
import { useGetMunicipalityQuery } from '@app/graphql';
import { Icon } from '@app/components';
import { editMunicipality } from '@app/utils/permissions';

export default function MunicipalityDetails() {
  const { navigate } = useLocation();
  const { municipalityId }: any = useParams();
  const { loading, data } = useGetMunicipalityQuery({
    id: municipalityId ? municipalityId : '',
  });
  const [municipalityData, setMunicipalityData] = React.useState<any>([]);
  const [title, updateTitle] = React.useState('');
  const [status, updateStatus] = React.useState('');
  const [showStatus, updateShowStatus] = React.useState(false);
  const { userInfo }: any = useContext(AuthContext);

  React.useEffect(() => {
    if (!loading && data && data.municipality && data.municipality.success) {
      if (data.municipality.municipality) {
        const municipalityData = data.municipality.municipality;
        setMunicipalityData(municipalityData);
        updateTitle(
          municipalityData && municipalityData.name
            ? municipalityData.name
            : 'Municipality Details',
        );
        updateStatus(
          municipalityData && municipalityData.status
            ? municipalityData.status
            : 'ACTIVE',
        );
        updateShowStatus(true);
      }
    }
  }, [loading, data]);

  return (
    <>
      <BackgroundContainer>
        {loading ? (
          <Loader>
            <Icon name="loader" />
          </Loader>
        ) : (
          <>
            <SecondaryHeader
              title={title}
              dataLabel="Municipality"
              showLabel={true}
              statusValue={status}
              showButton={false}
              onBackClick={() => navigate('/municipalities')}
              primaryButtonText="Edit Municipality"
              disabledPrimaryButton={false}
              showPrimaryButton={editMunicipality(userInfo) ? true : false}
              onPrimaryButtonClick={() =>
                navigate('/municipalities/edit/' + municipalityId)
              }
              showStatus={showStatus}
              statusColor={status === 'ACTIVE' ? 'primary' : 'secondary'}
            />
            <Container>
              <MunicipalityContainer>
                <Heading>Municipality Information</Heading>
                <LogoInfoCard>
                  <LogoIcon>Logo</LogoIcon>
                  <IconItem>
                    <MunicipalityLogo>
                      <MuncipalityIcon
                        image={
                          municipalityData.logo ? municipalityData.logo : ''
                        }
                      />
                    </MunicipalityLogo>
                  </IconItem>
                </LogoInfoCard>
                <InfoCard>
                  <InfoItem>
                    <Label>Municipality</Label>
                    <Value>
                      {municipalityData.name ? municipalityData.name : '- -'}
                    </Value>
                  </InfoItem>
                  <InfoItem>
                    <Label>State</Label>
                    <Value>
                      {municipalityData.stateInfo
                        ? municipalityData.stateInfo.label
                        : '- -'}
                    </Value>
                  </InfoItem>
                  <InfoItem>
                    <Label>County</Label>
                    <Counties>
                      {municipalityData.counties &&
                      municipalityData.counties.length ? (
                        municipalityData.counties.map(
                          (county: any, index: number) => {
                            return (
                              <Value>
                                {county.label || '- -'}
                                {index < municipalityData.counties.length - 1
                                  ? ' ,'
                                  : null}
                              </Value>
                            );
                          },
                        )
                      ) : (
                        <Value> - -</Value>
                      )}
                    </Counties>
                  </InfoItem>
                </InfoCard>
                <PrimaryInfoCard>
                  <InfoItem>
                    <Label>City</Label>
                    <Value>
                      {data.municipality.municipality.city
                        ? data.municipality.municipality.city
                        : '- -'}
                    </Value>
                  </InfoItem>
                  <InfoItem>
                    <Label>Address</Label>
                    <Value>
                      {data.municipality.municipality.address
                        ? data.municipality.municipality.address
                        : '- -'}
                    </Value>
                  </InfoItem>
                  <InfoItem>
                    <Label>Minimum Inspection Days</Label>
                    <Value>
                      {data.municipality.municipality.inspectionDaysMin
                        ? data.municipality.municipality.inspectionDaysMin
                        : '- -'}
                    </Value>
                  </InfoItem>
                </PrimaryInfoCard>
                <PrimaryInfoCard>
                  <EmailInfoItem>
                    <Label>Email</Label>
                    <Value>
                      {data.municipality.municipality.email
                        ? data.municipality.municipality.email
                        : '- -'}
                    </Value>
                  </EmailInfoItem>
                  <InfoItem>
                    <Label>Phone Number</Label>
                    <Value>
                      {data.municipality.municipality.phoneNumber
                        ? numberFormat(
                            data.municipality.municipality.phoneNumber,
                          )
                        : '- -'}
                    </Value>
                  </InfoItem>
                  <InfoItem>
                    <Label>Website</Label>
                    <Value>
                      {data.municipality.municipality.website ? (
                        <a
                          href={data.municipality.municipality.website}
                          target="/">
                          {' '}
                          {data.municipality.municipality.website}
                        </a>
                      ) : (
                        '- -'
                      )}
                    </Value>
                  </InfoItem>
                </PrimaryInfoCard>
              </MunicipalityContainer>
              <InfoBox>
                <PrimaryHeading>Title of Transfer Document</PrimaryHeading>
                <DetailsBox>
                  <Value>
                    {data.municipality.municipality.transferDocumentTitle
                      ? data.municipality.municipality.transferDocumentTitle
                      : '- -'}
                  </Value>
                </DetailsBox>
              </InfoBox>
              <InfoBox>
                <PrimaryHeading>Instructions and Notes</PrimaryHeading>
                <InstCard>
                  <Value>
                    {data.municipality.municipality.instructionsAndNotes
                      ? data.municipality.municipality.instructionsAndNotes
                      : '- -'}
                  </Value>
                </InstCard>
              </InfoBox>
              <InfoBox>
                <PrimaryHeading>Include “Legal Description”?</PrimaryHeading>

                <PrimaryDetailsBox>
                  <Label>
                    Does this municipality want to include this section in their
                    process?
                  </Label>
                  <DetailsCard>
                    <Value>
                      {data.municipality.municipality.includeLegalDescription
                        ? data.municipality.municipality.includeLegalDescription
                            .charAt(0)
                            .toUpperCase() +
                          data.municipality.municipality.includeLegalDescription.slice(
                            1,
                          )
                        : '- -'}
                    </Value>
                  </DetailsCard>
                </PrimaryDetailsBox>
              </InfoBox>
              <InfoBox>
                <PrimaryHeading>Qualifying Exemptions</PrimaryHeading>
                <DetailsBox>
                  <PrimaryInfoBox>
                    {data.municipality.municipality.qualifyingExemptions.map(
                      (data: any, i: any) => {
                        return (
                          <MultiInfoCard key={i}>
                            <PrimaryInfoItem>
                              <NumText>{i + 1}.</NumText>
                              <Value>
                                {data.exemption ? data.exemption : '- -'}
                              </Value>
                            </PrimaryInfoItem>
                          </MultiInfoCard>
                        );
                      },
                    )}
                  </PrimaryInfoBox>
                </DetailsBox>
              </InfoBox>
              <InfoBox>
                <PrimaryHeading>Nomenclature of the Tax</PrimaryHeading>
                <DetailsBox>
                  <Value>
                    {data.municipality.municipality.nomenclatureOfTheTax
                      ? data.municipality.municipality.nomenclatureOfTheTax
                      : '- -'}
                  </Value>
                </DetailsBox>
              </InfoBox>
              <InfoBox>
                <PrimaryHeading>Tax Calculations</PrimaryHeading>
                <PrimaryDetailsBox>
                  <Label>Tax Rate</Label>
                  <DetailsCard>
                    <TaxRate>
                      <Value>
                        {data.municipality.municipality.TaxCalculations.rate
                          ? data.municipality.municipality.TaxCalculations.rate
                          : '-'}
                      </Value>
                    </TaxRate>
                    <NumText>per $1000 of full actual consideration</NumText>
                  </DetailsCard>
                </PrimaryDetailsBox>
                <PrimaryDetailsBox>
                  <Label>Tax Exemption</Label>
                  <DetailsCard>
                    <TaxRate>
                      <Value>
                        {data.municipality.municipality.taxExemption
                          ? `$${data.municipality.municipality.taxExemption}`
                          : '-'}
                      </Value>
                    </TaxRate>
                  </DetailsCard>
                </PrimaryDetailsBox>
                <DetailsBox>
                  <PrimaryInfoBox>
                    <Label>Instructions</Label>
                    <Value>
                      {data.municipality.municipality.TaxCalculations
                        .instructions
                        ? data.municipality.municipality.TaxCalculations
                            .instructions
                        : '- -'}
                    </Value>
                  </PrimaryInfoBox>
                </DetailsBox>
              </InfoBox>
              <InfoBox>
                <PrimaryHeading>Proof of Tax Payment</PrimaryHeading>
                <PrimaryDetailsBox>
                  <Label>
                    Does the municipality require proof of tax payment?
                  </Label>
                  <DetailsCard>
                    <Value>
                      {data.municipality.municipality.proofofTaxPayment.required
                        ? data.municipality.municipality.proofofTaxPayment.required
                            .charAt(0)
                            .toUpperCase() +
                          data.municipality.municipality.proofofTaxPayment.required.slice(
                            1,
                          )
                        : '- -'}
                    </Value>
                  </DetailsCard>
                </PrimaryDetailsBox>
                {data.municipality.municipality.proofofTaxPayment.required ===
                'yes' ? (
                  <DetailsBox>
                    <PrimaryInfoBox>
                      <Label>Instructions</Label>
                      <Value>
                        {data.municipality.municipality.proofofTaxPayment
                          .instructions
                          ? data.municipality.municipality.proofofTaxPayment
                              .instructions
                          : '- -'}
                      </Value>
                    </PrimaryInfoBox>
                  </DetailsBox>
                ) : (
                  ''
                )}
              </InfoBox>
              <InfoBox>
                <PrimaryHeading>Nomenclature of PIN</PrimaryHeading>
                <DetailsBox>
                  <Value>
                    {data.municipality.municipality.nomenclatureOfThePIN
                      ? data.municipality.municipality.nomenclatureOfThePIN
                      : '- -'}
                  </Value>
                </DetailsBox>
              </InfoBox>

              <InfoBox>
                <PrimaryHeading>Require Water Reading?</PrimaryHeading>
                <PrimaryDetailsBox>
                  <Label>
                    Does the municipality require a final water reading of the
                    property?
                  </Label>
                  <DetailsCard>
                    <Value>
                      {data.municipality.municipality.waterReading.required
                        ? data.municipality.municipality.waterReading.required
                            .charAt(0)
                            .toUpperCase() +
                          data.municipality.municipality.waterReading.required.slice(
                            1,
                          )
                        : '- -'}
                    </Value>
                  </DetailsCard>
                </PrimaryDetailsBox>
                {data.municipality.municipality.waterReading.required ===
                'yes' ? (
                  <DetailsBox>
                    <PrimaryInfoBox>
                      <Label> Water Reading Instructions</Label>
                      <Value>
                        {data.municipality.municipality.waterReading
                          .instructions
                          ? data.municipality.municipality.waterReading
                              .instructions
                          : '- -'}
                      </Value>
                    </PrimaryInfoBox>
                  </DetailsBox>
                ) : (
                  ''
                )}
              </InfoBox>
              <InfoBox>
                <PrimaryHeading>Require Inspection?</PrimaryHeading>
                <PrimaryDetailsBox>
                  <Label>
                    Does the municipality require “point of sale” inspection of
                    the property?
                  </Label>
                  <DetailsCard>
                    <Value>
                      {data.municipality.municipality.inspection.required
                        ? data.municipality.municipality.inspection.required
                            .charAt(0)
                            .toUpperCase() +
                          data.municipality.municipality.inspection.required.slice(
                            1,
                          )
                        : '- -'}
                    </Value>
                  </DetailsCard>
                </PrimaryDetailsBox>
                {data.municipality.municipality.inspection.required ===
                'yes' ? (
                  <>
                    <PrimaryDetailsBox>
                      <Label>Inspection Instructions</Label>
                      <DetailsCard>
                        <Value>
                          {data.municipality.municipality.inspection
                            .instructions
                            ? data.municipality.municipality.inspection
                                .instructions
                            : '- -'}
                        </Value>
                      </DetailsCard>
                    </PrimaryDetailsBox>
                    <DetailsBox>
                      <PrimaryInfoBox>
                        <Label>Allow for Inspection Document Upload?</Label>
                        <Value>
                          {data.municipality.municipality.inspection
                            .allowUsersToUploadDocs
                            ? data.municipality.municipality.inspection.allowUsersToUploadDocs
                                .charAt(0)
                                .toUpperCase() +
                              data.municipality.municipality.inspection.allowUsersToUploadDocs.slice(
                                1,
                              )
                            : '- -'}
                        </Value>
                      </PrimaryInfoBox>
                    </DetailsBox>
                  </>
                ) : (
                  ''
                )}
              </InfoBox>
              <InfoBox>
                <PrimaryHeading>
                  Nomenclature of the Recorder’s Box
                </PrimaryHeading>
                <DetailsBox>
                  <Value>
                    {data.municipality.municipality.nomenclatureOfTheRecorderBox
                      ? data.municipality.municipality
                          .nomenclatureOfTheRecorderBox
                      : '- -'}
                  </Value>
                </DetailsBox>
              </InfoBox>
              <InfoBox>
                <PrimaryHeading>
                  {' '}
                  Information in the Recorder’s Box
                </PrimaryHeading>
                <DetailsBox>
                  <PrimaryInfoBox>
                    {data.municipality.municipality.informationInTheRecordersBox.map(
                      (data: any, i: any) => {
                        return (
                          <MultiInfoCard key={i}>
                            <PrimaryInfoItem>
                              <NumText>{i + 1}.</NumText>
                              <Value>{data ? data : '- -'}</Value>
                            </PrimaryInfoItem>
                          </MultiInfoCard>
                        );
                      },
                    )}
                  </PrimaryInfoBox>
                </DetailsBox>
              </InfoBox>
              <InfoBox>
                <PrimaryHeading>Additional Questions</PrimaryHeading>
                <DetailsBox>
                  <PrimaryInfoBox>
                    {data.municipality.municipality.additionalQuestions.map(
                      (data: any, i: any) => {
                        return (
                          <MultiInfoCard key={i}>
                            <PrimaryInfoItem>
                              <NumText>{i + 1}.</NumText>
                              <Value>
                                {data && data.question ? data.question : '- -'}
                              </Value>
                            </PrimaryInfoItem>
                          </MultiInfoCard>
                        );
                      },
                    )}
                  </PrimaryInfoBox>
                </DetailsBox>
              </InfoBox>
            </Container>
          </>
        )}
      </BackgroundContainer>
    </>
  );
}
